import env from '../config/env.js';

// Mirrors server/utils/canonicalUrl.js. Every absolute URL the page advertises
// (canonical, og:url) is built here so the tag can't name a different host than
// the one the visitor is actually on.

function stripTrailingSlash(value) {
  return String(value || '').trim().replace(/\/+$/, '');
}

function parseUrl(value) {
  try {
    return new URL(String(value).trim());
  } catch {
    return null;
  }
}

// Canonicals always use the apex hostname, regardless of whether an admin,
// environment variable, or visitor supplied the www variant.
function withoutWww(url) {
  if (!url.hostname.toLowerCase().startsWith('www.')) return url;
  url.hostname = url.hostname.slice(4);
  return url;
}

// Two hosts are the same site when they differ only by the www prefix.
function isSameSite(a, b) {
  const bare = (host) => String(host || '').toLowerCase().replace(/^www\./, '');
  return bare(a) === bare(b) && bare(a) !== '';
}

// Preferred origin: admin "Canonical URL" setting, then the build's site URL,
// then whatever host the visitor is on.
export function canonicalOrigin(configured) {
  const candidates = [configured, env.SITE_URL, window.location.origin];
  for (const c of candidates) {
    const parsed = parseUrl(stripTrailingSlash(c));
    if (parsed) return withoutWww(parsed).origin;
  }
  return '';
}

// Re-points an absolute URL at the canonical host when it differs only by the
// www prefix or the scheme. A different domain is left alone (deliberate
// cross-domain canonical).
export function normalizeToOrigin(value, origin) {
  const url = parseUrl(value);
  const base = parseUrl(origin);
  if (!url || !base || !isSameSite(url.hostname, base.hostname)) return value;
  const path = url.pathname === '/' ? '/' : stripTrailingSlash(url.pathname);
  return `${base.origin}${path}${url.search}`;
}

export function buildAbsoluteUrl(pathOrUrl, origin) {
  const base = stripTrailingSlash(origin);
  if (!pathOrUrl) return base;
  if (/^https?:\/\//i.test(pathOrUrl)) return normalizeToOrigin(pathOrUrl, base);
  const path = String(pathOrUrl).startsWith('/') ? pathOrUrl : `/${pathOrUrl}`;
  return base ? `${base}${path}` : path;
}
